import React, { FC, useMemo, useState } from 'react';
import { Table, Tooltip } from 'antd';
import { ColumnType } from 'antd/es/table';
import moment from 'moment';
import { useAppSelector } from 'app/store/store.hooks';
import {
  EStockReconciliationUoM,
  IStockReconciliationShiftListItem,
} from 'entities/StockReconciliation/StockReconciliation.models';
import {
  STOCK_RECONCILIATION_DASH,
  STOCK_RECONCILIATION_DATA_UNAVAILABLE_TOOLTIP,
  STOCK_RECONCILIATION_SHIFTS_LIMIT,
} from 'entities/StockReconciliation/StockReconciliation.const';
import {
  formatStockReconciliationNumber,
  formatStockReconciliationPercent,
  formatStockReconciliationTime,
} from 'entities/StockReconciliation/StockReconciliation.helper';
import { StockReconciliationItemsTable } from 'entities/StockReconciliation/components/StockReconciliationItemsTable';

interface IProps {
  uom: EStockReconciliationUoM;
  page: number;
  onPageChange: (page: number) => void;
}

const renderNullablePercent = (value: number | null) => {
  if (value === null || value === undefined) {
    return (
      <Tooltip title={STOCK_RECONCILIATION_DATA_UNAVAILABLE_TOOLTIP}>
        <span>{STOCK_RECONCILIATION_DASH}</span>
      </Tooltip>
    );
  }
  return formatStockReconciliationPercent(value);
};

const renderNullableString = (value: string | null) => {
  if (!value) {
    return (
      <Tooltip title={STOCK_RECONCILIATION_DATA_UNAVAILABLE_TOOLTIP}>
        <span>{STOCK_RECONCILIATION_DASH}</span>
      </Tooltip>
    );
  }
  return value;
};

const renderVariance = (value: number | null) => {
  if (value === null || value === undefined) {
    return (
      <Tooltip title={STOCK_RECONCILIATION_DATA_UNAVAILABLE_TOOLTIP}>
        <span>{STOCK_RECONCILIATION_DASH}</span>
      </Tooltip>
    );
  }
  const formatted = formatStockReconciliationNumber(value);
  return <span style={{ color: value !== 0 ? '#d4380d' : undefined }}>{formatted}</span>;
};

export const StockReconciliationShiftsTable: FC<IProps> = ({ uom, page, onPageChange }) => {
  const { stockReconciliationShiftCollection, stockReconciliationShiftCollectionLoading } = useAppSelector(
    (state) => state.stockReconciliation
  );

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  const columns = useMemo<ColumnType<IStockReconciliationShiftListItem>[]>(
    () => [
      { key: 'weekLabel', dataIndex: 'weekLabel', title: 'WK' },
      {
        key: 'day',
        dataIndex: 'day',
        title: 'DAY',
        render: (value: string) => {
          const m = moment(value);
          return m.isValid() ? m.format('ddd, MMM D') : value;
        },
      },
      { key: 'shiftId', dataIndex: 'shiftId', title: 'SHIFT', render: renderNullableString },
      { key: 'startAt', dataIndex: 'startAt', title: 'START', render: (v: string | null) => formatStockReconciliationTime(v) },
      { key: 'endAt', dataIndex: 'endAt', title: 'END', render: (v: string | null) => formatStockReconciliationTime(v) },
      { key: 'crew', dataIndex: 'crew', title: 'CREW', render: renderNullableString },
      { key: 'witness', dataIndex: 'witness', title: 'WITNESS', render: renderNullableString },
      { key: 'varTotal', dataIndex: 'varTotal', title: 'VAR', render: renderVariance },
      { key: 'positivePercent', dataIndex: 'positivePercent', title: 'Positive %', render: renderNullablePercent },
      { key: 'negativePercent', dataIndex: 'negativePercent', title: 'Negative %', render: renderNullablePercent },
    ],
    []
  );

  const total = stockReconciliationShiftCollection?.meta.count ?? 0;

  return (
    <Table<IStockReconciliationShiftListItem>
      rowKey="shiftKey"
      columns={columns}
      dataSource={stockReconciliationShiftCollection?.data ?? []}
      loading={stockReconciliationShiftCollectionLoading}
      size="middle"
      pagination={{
        current: page,
        pageSize: STOCK_RECONCILIATION_SHIFTS_LIMIT,
        total,
        onChange: onPageChange,
        showSizeChanger: false,
      }}
      expandable={{
        expandedRowKeys: expandedKeys,
        onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
        expandedRowRender: (record) => <StockReconciliationItemsTable shiftKey={record.shiftKey} uom={uom} />,
      }}
      locale={{ emptyText: 'No data for selected week' }}
    />
  );
};
